import React from 'react';
import { AlertCircle, Download, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useGeneratedFileDownload } from '@CustomHooks';
import { GeneratedFile } from '@Models';
import { FileDownloadButtonVariant, fileDownloadButtonVariants } from './variants';

interface FileDownloadButtonProps {
  file: GeneratedFile;
  conversationId: string;
  timestamp: string;
  /** Position in a sibling list — drives the staggered fade-up animation. */
  index?: number;
  /** Extra seconds added so the cascade can chain after sibling elements (e.g. option cards). */
  staggerOffset?: number;
}

const FileDownloadButton: React.FC<FileDownloadButtonProps> = ({
  file,
  conversationId,
  timestamp,
  index = 0,
  staggerOffset = 0,
}) => {
  const { t } = useTranslation();
  const { download, isPending, error, formatFileSize } = useGeneratedFileDownload(conversationId, timestamp, file);

  const getFileTypeVariant = (): FileDownloadButtonVariant => {
    switch (file.file_type) {
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      case 'application/vnd.ms-excel':
        return 'excel';
      case 'text/csv':
        return 'csv';
      case 'application/pdf':
        return 'pdf';
      case 'image/png':
      case 'image/jpeg':
        return 'image';
      case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      case 'application/vnd.ms-powerpoint':
        return 'presentation';
      case 'application/json':
        return 'json';
      case 'text/plain':
        return 'text';
      default:
        return 'default';
    }
  };

  const styles = fileDownloadButtonVariants({ fileType: getFileTypeVariant() });

  return (
    <div className={styles.container()} style={{ animationDelay: `${index * 0.08 + 0.05 + staggerOffset}s` }}>
      <button type="button" onClick={download} disabled={isPending} className={styles.button()}>
        <FileText className={styles.fileIcon()} />
        <div className={styles.fileInfo()}>
          <span className={styles.fileName()}>{file.file_name}</span>
          <span className={styles.fileSize()}>{formatFileSize(file.file_size)}</span>
        </div>
        {isPending ? <div className={styles.spinner()} /> : <Download className={styles.downloadIcon()} />}
      </button>

      {error && (
        <div className={styles.errorContainer()}>
          <AlertCircle className={styles.errorIcon()} />
          <span>{t('Agent.FileDownloadError')}</span>
        </div>
      )}
    </div>
  );
};

export default FileDownloadButton;
